import 'images/select-arrow.svg';

import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import numeral from 'numeral';

import { translate } from 'helpers/Translate';

/**
 * Regex to determine if a string is a number
 * @type {RegExp}
 */
const IS_NUMBER = /^[-+]?[0-9]+$/;

/**
 * formatValue function to format the type of the data to send
 * @param  {Object} event - onChange or onBlur event
 * @return {String|Number} - Return formatted value
 */
const sanitizeValue = (value) => {
    if (IS_NUMBER.test(value)) {
        return numeral(value).value();
    }

    if (value === '') {
        return null;
    }

    return value;
};

/**
 * Select field
 * @param {Object} input - Redux form input
 * @param {Array} options - Select options
 */
const Select = ({
    containerClassName,
    wrapperClassName,
    input,
    meta: { active, touched, error },
    options,
    emptyOption,
    emptyValue,
    emptyLabel,
    small,
    fullWidth,
}) => {
    const isError = touched;

    const containerClasses = classnames({
        'field-container': true,
        [containerClassName]: !!containerClassName,
    });

    const wrapperClasses = classnames({
        field: true,
        'field--small-height': small,
        'field--full-width': fullWidth,
        'field--active': active,
        'field--invalid': isError && error,
        [wrapperClassName]: !!wrapperClassName,
    });

    const inputClasses = classnames({
        field__control: true,
        'field__control--select': true,
    });

    return (
        <div className={containerClasses}>
            <div className={wrapperClasses}>
                <select
                    className={inputClasses}
                    id={input.name}
                    value={String(input.value)}
                    onFocus={input.onFocus}
                    onChange={(e) => input.onChange(sanitizeValue(e.target.value))}
                    onBlur={(e) => input.onBlur(sanitizeValue(e.target.value))}
                >
                    {emptyOption && (
                        <option key="empty-value" data-empty-value value={emptyValue || ''}>
                            {translate(`-- ${emptyLabel} --`)}
                        </option>
                    )}
                    {options.map((option) => (
                        <option key={option.index || option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>
            {isError && error && <p className="field-error">{error}</p>}
        </div>
    );
};

Select.displayName = 'Select';

Select.propTypes = {
    emptyLabel: PropTypes.string,
    emptyOption: PropTypes.bool,
    emptyValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    options: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    input: PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        onBlur: PropTypes.func.isRequired,
        onChange: PropTypes.func.isRequired,
    }).isRequired,
    wrapperClassName: PropTypes.string,
    small: PropTypes.bool,
};

Select.defaultProps = {
    emptyOption: false,
    emptyValue: '',
    emptyLabel: 'Choisir',
};

/**
 * Select Functionnal Component
 */
export default Select;
